"use client";

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import MCMCombobox, { type ComponentLink } from "@/components/ui/MCMCombobox";
import { Button } from "@/components/ui/button";
import { useTenancy } from "@/contexts/TenancyContext";
import { MCMService } from "@/services/mcmService";

export default function MCMComboboxAssetsOnlyExample() {
  const [links, setLinks] = React.useState<ComponentLink[]>([]);
  const { activeTenancy } = useTenancy();
  const [saving, setSaving] = React.useState(false);
  const targetControls = React.useMemo(() => (
    [
      { id: "A.8.1", label: "Asset Management – A.8.1" },
      { id: "A.8.2", label: "Ownership of Assets – A.8.2" },
    ]
  ), []);

  const busy = saving || !activeTenancy?.id || links.length === 0;

  const applyToMCM = async () => {
    if (!activeTenancy?.id || links.length === 0) return;
    setSaving(true);
    try {
      await MCMService.bulkUpdate({
        controlIds: targetControls.map(t => t.id),
        type: 'linkedComponents',
        action: 'add',
        data: { links }
      }, activeTenancy.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>MCM Combobox — Assets Only, No Primary Control</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <MCMCombobox
            requirePrimary={false}
            allowedCategories={["assets"]}
            targetControlIds={targetControls.map(t => t.id)}
            targetControls={targetControls}
            selected={links}
            onChange={setLinks}
            placeholder="Select assets…"
            fullWidth
          />

          <div className="flex items-center gap-2">
            <Badge variant="secondary">Selected</Badge>
            <span className="text-sm text-muted-foreground">{links.length} item{links.length !== 1 ? "s" : ""}</span>
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-sm font-medium">Payload</div>
            <pre className="text-xs bg-muted rounded-md p-3 overflow-auto max-h-[320px]">
{JSON.stringify({ controlIDs: targetControls.map(t => t.id), linkedComponents: links }, null, 2)}
            </pre>
          </div>

          <div className="flex justify-end">
            <Button disabled={busy} onClick={applyToMCM}>
              {saving ? 'Saving…' : 'Apply to MCM'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
